import type {
  ActiveRow,
  AttentionRow,
  QueuedRow,
  WorkspaceTaskSummary,
} from '@/features/tasks';
import { READER_WAITING_LABEL } from '@/features/tasks/statusPresentation';
import type {
  LinkedTaskSummaryItem,
  TaskView,
} from '@/lib/api/generated/model';

/**
 * Work in flight, grouped by what it serves (spec 150 US4).
 *
 * A pure reading of the overview's summary: one row per top-level task that is
 * running, queued or waiting on its subtasks, with those subtasks nested under
 * it — never as rows of their own beside it. Nothing here formats, links or
 * remembers; the section does that.
 */

/** What a nested subtask is doing, in the overview's words. */
export type InFlightChildState =
  | 'needs-attention'
  | 'waiting-on-you'
  | 'running'
  | 'queued'
  | 'waiting-on-subtasks'
  | 'in-progress'
  | 'not-started'
  | 'done';

/** The stuck end of a parent's batch, in the order a reader should act on it. */
export type InFlightWorst =
  'needs-attention' | 'waiting-on-you' | 'running' | 'waiting' | 'done';

export interface InFlightChild {
  taskId: string;
  title: string;
  state: InFlightChildState;
  /** The child's own attention row, so the section can offer the same action. */
  attention?: AttentionRow;
  /** When it runs: since when. */
  active?: ActiveRow;
  queued?: QueuedRow;
  /**
   * A child that waits on subtasks of its own shows their count, not them:
   * nesting stops at one level (spec 150 FR-006).
   */
  grandchildren?: { done: number; total: number };
}

export interface InFlightParent {
  row: ActiveRow | QueuedRow;
  /** `true` for a queued top-level task: it waits for a slot. */
  queued: boolean;
  /** Empty for a plain running or queued task. */
  children: InFlightChild[];
  /** The batch the bar draws, when the task waits on subtasks. */
  batch: LinkedTaskSummaryItem[] | null;
  batchTotal: number;
  worst: InFlightWorst | null;
  defaultOpen: boolean;
}

export interface InFlight {
  rows: InFlightParent[];
  /** Top-level rows beyond the cap. */
  overflow: number;
}

/** Cards are a third of a row's height, so In motion holds more (spec 153). */
export const IN_FLIGHT_CAP = 8;

const WORST_RANK: Record<InFlightWorst, number> = {
  'needs-attention': 0,
  'waiting-on-you': 1,
  running: 2,
  waiting: 3,
  done: 4,
};

/** A parent's batch, read from the summary's own rows wherever it has one. */
function childrenOf(
  parent: TaskView,
  index: SummaryIndex,
  tasksById: ReadonlyMap<string, TaskView>,
): InFlightChild[] {
  return (parent.linked_tasks?.tasks ?? []).map((item) => {
    const base = { taskId: item.task_id, title: item.title };
    const attention = index.attention.get(item.task_id);
    if (attention) {
      return {
        ...base,
        attention,
        state: index.needsAttention.has(item.task_id)
          ? ('needs-attention' as const)
          : ('waiting-on-you' as const),
      };
    }
    const active = index.active.get(item.task_id);
    if (active) {
      if (active.state === 'waiting-on-subtasks') {
        const own = tasksById.get(item.task_id)?.linked_tasks;
        return {
          ...base,
          active,
          state: 'waiting-on-subtasks' as const,
          ...(own
            ? { grandchildren: { done: own.done, total: own.total } }
            : {}),
        };
      }
      return { ...base, active, state: 'running' as const };
    }
    const queued = index.queued.get(item.task_id);
    if (queued) return { ...base, queued, state: 'queued' as const };
    // Nothing on the page says more than the batch does: read its status.
    switch (item.status) {
      case 'done':
        return { ...base, state: 'done' as const };
      case 'not_started':
        return { ...base, state: 'not-started' as const };
      case 'needs_attention':
        return { ...base, state: 'needs-attention' as const };
      case 'needs_review':
        return { ...base, state: 'waiting-on-you' as const };
      case 'in_progress':
        return { ...base, state: 'in-progress' as const };
      default: {
        // Exhaustive over the contract's statuses: a new one fails type-check
        // here (review of !254). One that reaches the page anyway reads as in
        // progress, and says so where someone can find it.
        const unknown: never = item.status;
        console.warn('in flight: subtask status not recognised', {
          taskId: item.task_id,
          status: unknown,
        });
        return { ...base, state: 'in-progress' as const };
      }
    }
  });
}

function worstOf(children: readonly InFlightChild[]): InFlightWorst | null {
  let worst: InFlightWorst | null = null;
  for (const child of children) {
    const rank: InFlightWorst =
      child.state === 'needs-attention'
        ? 'needs-attention'
        : child.state === 'waiting-on-you'
          ? 'waiting-on-you'
          : child.state === 'running'
            ? 'running'
            : child.state === 'done'
              ? 'done'
              : 'waiting';
    if (worst === null || WORST_RANK[rank] < WORST_RANK[worst]) worst = rank;
  }
  return worst;
}

interface SummaryIndex {
  attention: Map<string, AttentionRow>;
  needsAttention: Set<string>;
  active: Map<string, ActiveRow>;
  queued: Map<string, QueuedRow>;
}

function indexOf(summary: WorkspaceTaskSummary): SummaryIndex {
  const attention = new Map<string, AttentionRow>();
  for (const row of summary.attention.waiting) attention.set(row.taskId, row);
  for (const row of summary.attention.needsAttention)
    attention.set(row.taskId, row);
  return {
    attention,
    needsAttention: new Set(
      summary.attention.needsAttention.map((row) => row.taskId),
    ),
    active: new Map(summary.active.map((row) => [row.taskId, row])),
    queued: new Map(summary.queued.map((row) => [row.taskId, row])),
  };
}

/** When a top-level row started waiting or running, for "longest first". */
function since(parent: InFlightParent): number {
  const at = parent.queued
    ? (parent.row as QueuedRow).queuedAt
    : (parent.row as ActiveRow).startedAt;
  const time = at ? Date.parse(at) : Number.NaN;
  return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time;
}

/**
 * The rank a top-level row sorts by: its worst child, else its own state. A
 * plain running task ranks as running; a parent whose batch is all quiet ranks
 * as waiting.
 */
function rankOf(parent: InFlightParent): number {
  const own = parent.queued
    ? WORST_RANK.waiting + 0.5
    : (parent.row as ActiveRow).state === 'running'
      ? WORST_RANK.running
      : WORST_RANK.waiting;
  return parent.worst === null ? own : Math.min(WORST_RANK[parent.worst], own);
}

export function inFlight(
  summary: WorkspaceTaskSummary,
  tasksById: ReadonlyMap<string, TaskView>,
): InFlight {
  const index = indexOf(summary);
  const inFlightIds = new Set([
    ...summary.active.map((row) => row.taskId),
    ...summary.queued.map((row) => row.taskId),
  ]);

  // A subtask nests under its parent when the parent is in flight too; one whose
  // parent is not (finished, filtered out, gone) stands on its own, breadcrumb
  // and all.
  const nestsUnderParent = (row: ActiveRow | QueuedRow) =>
    row.parent !== undefined &&
    row.parent.reachable &&
    inFlightIds.has(row.parent.taskId);

  const tops: InFlightParent[] = [
    ...summary.active
      .filter((row) => !nestsUnderParent(row))
      .map((row): InFlightParent => {
        const task = tasksById.get(row.taskId);
        const waiting = row.state === 'waiting-on-subtasks' && task;
        const children = waiting ? childrenOf(task, index, tasksById) : [];
        const worst = worstOf(children);
        return {
          row,
          queued: false,
          children,
          batch: waiting ? (task.linked_tasks?.tasks ?? []) : null,
          batchTotal: waiting ? (task.linked_tasks?.total ?? 0) : 0,
          worst,
          // Closed until the person opens it (spec 153 FR-008): a stuck
          // subtask is already in Your turn, and opening it here repeated it.
          defaultOpen: false,
        };
      }),
    ...summary.queued
      .filter((row) => !nestsUnderParent(row))
      .map((row): InFlightParent => ({
        row,
        queued: true,
        children: [],
        batch: null,
        batchTotal: 0,
        worst: null,
        defaultOpen: false,
      })),
  ];

  tops.sort((a, b) => rankOf(a) - rankOf(b) || since(a) - since(b));

  return {
    rows: tops.slice(0, IN_FLIGHT_CAP),
    overflow: Math.max(tops.length - IN_FLIGHT_CAP, 0),
  };
}

/** The order a parent's batch is said in: finished work first, unstarted last. */
const WORD_ORDER = [
  'done',
  'failed',
  'stopped',
  'stopped by a spend limit',
  'waiting for a personal account',
  'off the board',
  'need attention',
  'with you',
  'running',
  'queued',
  'waiting on subtasks',
  'in progress',
  'to start',
] as const;

type BatchWord = (typeof WORD_ORDER)[number];

/** One child's word in its parent's breakdown. */
function wordOf(child: InFlightChild): BatchWord {
  switch (child.state) {
    case 'done':
      return 'done';
    case 'needs-attention':
      switch (child.attention?.kind) {
        case 'failed-run':
        case 'failed-workdir':
          return 'failed';
        case 'stopped':
          return 'stopped';
        case 'spend-limit':
          return 'stopped by a spend limit';
        case 'needs-account':
          return 'waiting for a personal account';
        case 'orphaned':
          return 'off the board';
        default:
          return 'need attention';
      }
    case 'waiting-on-you':
      return 'with you';
    case 'running':
      return 'running';
    case 'queued':
      return 'queued';
    case 'waiting-on-subtasks':
      return 'waiting on subtasks';
    case 'in-progress':
      return 'in progress';
    case 'not-started':
      return 'to start';
  }
}

/**
 * A parent's batch in words, finished work first: "1 done, 1 stopped, 2 with
 * you" (spec 153 FR-003). Empty when the batch lists nobody.
 *
 * `canWork` is `useCanWork()`: a reader cannot answer a gate, so their "with
 * you" is "waiting on a member" (spec 193).
 */
export function breakdownWords(
  children: readonly InFlightChild[],
  canWork = true,
): string {
  const counts = new Map<BatchWord, number>();
  for (const child of children) {
    const word = wordOf(child);
    counts.set(word, (counts.get(word) ?? 0) + 1);
  }
  return WORD_ORDER.filter((word) => counts.has(word))
    .map((word) => {
      const said =
        word === 'with you' && !canWork
          ? READER_WAITING_LABEL.toLowerCase()
          : word;
      return `${String(counts.get(word))} ${said}`;
    })
    .join(', ');
}
